import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { AIConciergeButton } from "./AIConciergeButton";
import { AIConciergePanel } from "./AIConciergePanel";
import { useAIConcierge, UserInfo } from "@/hooks/useAIConcierge";

const STORAGE_KEY = "hibane_concierge_user";

export const AIConcierge = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const location = useLocation();
  const { messages, isLoading, error, sendMessage, clearMessages, saveConversation } = useAIConcierge(userInfo);

  // Load user info from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.name && parsed.email) {
          setUserInfo(parsed);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Save user info to localStorage when set
  const handleUserInfoSubmit = (info: UserInfo) => {
    setUserInfo(info);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(info));
    } catch {
      // Ignore storage errors
    }
  };

  // Hide on admin and participant dashboard pages
  const hiddenPaths = ["/admin", "/participant"];
  const shouldHide = hiddenPaths.some((path) => location.pathname.startsWith(path));

  // Also hide on splash screen
  if (location.pathname === "/" || shouldHide) {
    return null;
  }

  const handleToggle = () => {
    // Save conversation when closing
    if (isOpen && messages.length >= 2) {
      saveConversation();
    }
    setIsOpen((prev) => !prev);
  };

  const handleClear = () => {
    clearMessages();
  };

  return (
    <>
      <AIConciergeButton 
        isOpen={isOpen} 
        onClick={handleToggle}
        hasUnread={false}
      />
      
      <AnimatePresence>
        {isOpen && (
          <AIConciergePanel
            messages={messages}
            isLoading={isLoading}
            error={error}
            onSendMessage={sendMessage}
            onClear={handleClear}
            userInfo={userInfo}
            onUserInfoSubmit={handleUserInfoSubmit}
          />
        )}
      </AnimatePresence>
    </>
  );
};
